import React, { useEffect, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../../styles/dashboardTheme.css";
import "../../../../styles/managementTheme.css";
import "../styles/estudiantes.css";
import ResumenCards from "../components/ResumenCards";
import EstudiantesFilters from "../components/EstudiantesFilters";
import EstudiantesTable from "../components/EstudiantesTable";
import EstudianteFormModal from "../components/EstudianteFormModal";
import { usePermissions } from "../../../../utils/permissions";
import { syncEstudiante } from "../../../../services/contificoService";
import { showToast } from "../../../../utils/showToast";
import estudiantesService from "../../../../services/estudiantesService";

export default function EstudiantesView({
  loading,
  error,
  resumen,
  filtros,
  setFiltros,
  limpiarFiltros,
  lastUpdated,
  page,
  pageSize,
  totalPages,
  total,
  goToPage,
  estudiantesPaginados,
  seleccionado,
  seleccionar,
  reload,
  upsertEstudiante,
  form,
}) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission("estudiantes.crear");
  const canUpdate = hasPermission("estudiantes.actualizar");
  const canDelete = hasPermission("estudiantes.eliminar");
  const [syncingId, setSyncingId]   = useState(null);
  const [modalPin, setModalPin]     = useState(null); // { nombre, pin, expira_horas }

  const handleCampusReset = useCallback(async (est) => {
    try {
      const res = await estudiantesService.campusReset(est.id);
      setModalPin({
        nombre: `${est.nombres} ${est.apellidos}`,
        pin: res.pin,
        expira_horas: res.expira_horas,
      });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "Error generando PIN" });
    }
  }, []);

  const handleSyncContifico = useCallback(async (est) => {
    if (syncingId) return;
    setSyncingId(est.id);
    try {
      const resultado = await syncEstudiante(est.id);
      upsertEstudiante({ id: est.id, contifico_persona_id: resultado.contifico_persona_id });
      showToast({ type: "success", message: `${est.nombres} ${est.apellidos} sincronizado con Contífico.` });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "Error al sincronizar con Contífico." });
    } finally {
      setSyncingId(null);
    }
  }, [syncingId, upsertEstudiante]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (form.modal) document.body.classList.add("modal-open");
    else document.body.classList.remove("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [form.modal]);

  const quickFilter = (key) => {
    switch (key) {
      case "activos":
        setFiltros((prev) => ({ ...prev, activo: "1", estado_academico: "all" }));
        break;
      case "inscritos":
        setFiltros((prev) => ({ ...prev, estado_academico: "inscrito", activo: "all" }));
        break;
      case "matriculados":
        setFiltros((prev) => ({ ...prev, estado_academico: "matriculado", activo: "all" }));
        break;
      case "egresados":
        setFiltros((prev) => ({ ...prev, estado_academico: "egresado", activo: "all" }));
        break;
      default:
        setFiltros((prev) => ({ ...prev, activo: "all", estado_academico: "all" }));
        break;
    }
  };

  const activeQuickFilter =
    filtros?.activo === "1"
      ? "activos"
      : filtros?.estado_academico === "inscrito"
        ? "inscritos"
        : filtros?.estado_academico === "matriculado"
          ? "matriculados"
          : filtros?.estado_academico === "egresado"
            ? "egresados"
            : "total";
  
            const lastSync = lastUpdated ? lastUpdated.toLocaleString() : "Sincronizacion inicial";
  return (
    <section className="management-page management-theme-violet">
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">Formacion academica</p>
          <h4 className="management-title mb-1">Gestion de estudiantes</h4>
          <p className="text-muted mb-0">
            Administra los estudiantes.
          </p>
        </div>
        <div className="d-flex flex-column align-items-end gap-2">
          {canCreate && (
            <button type="button" className="btn management-btn-primary" onClick={form.abrirCrear}>
              <i className="bi bi-plus-circle me-2" />
              Nuevo estudiante
            </button>
          )}
          <small className="text-muted">Actualizado: {lastSync}</small>
        </div>
      </div>

      <ResumenCards resumen={resumen} onQuickFilter={quickFilter} activeKey={activeQuickFilter} />

      <EstudiantesFilters filtros={filtros} setFiltros={setFiltros} limpiarFiltros={limpiarFiltros} />

      <EstudiantesTable
        estudiantes={estudiantesPaginados}
        loading={loading}
        error={error}
        seleccionadoId={seleccionado?.id}
        onSelect={seleccionar}
        onEdit={canUpdate ? form.abrirEditar : undefined}
        onDelete={canDelete ? form.abrirEliminar : undefined}
        onViewDocs={(est) => navigate(`/dashboard/estudiantes/${est.id}/detalle?tab=documentos`)}
        onViewDetail={(est) => navigate(`/dashboard/estudiantes/${est.id}/detalle`)}
        onSyncContifico={handleSyncContifico}
        onCampusReset={canUpdate ? handleCampusReset : undefined}
        canEdit={canUpdate}
        canDelete={canDelete}
        reload={reload}
      />

      <div className="management-card d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div className="text-muted small">
          Mostrando {total === 0 ? 0 : (page - 1) * pageSize + 1}-{Math.min(total, page * pageSize)} de {total}
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="btn-group">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
            >
              <i className="bi bi-chevron-left" />
            </button>
            <button className="btn btn-outline-secondary btn-sm" disabled>
              Pagina {page} / {totalPages}
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages}
            >
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        </div>
      </div>

      <EstudianteFormModal {...form} />

      {/* Modal PIN reset Campus */}
      {modalPin && (
        <div className="modal d-block" style={{ background: "rgba(0,0,0,.5)" }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h6 className="modal-title fw-bold">
                  <i className="bi bi-key-fill text-warning me-2" />
                  PIN de acceso Campus
                </h6>
                <button type="button" className="btn-close" onClick={() => setModalPin(null)} />
              </div>
              <div className="modal-body text-center py-4">
                <p className="text-muted small mb-3">
                  Estudiante: <strong>{modalPin.nombre}</strong>
                </p>
                <div
                  className="d-inline-flex align-items-center gap-3 px-4 py-3 rounded-3 border border-warning bg-warning bg-opacity-10 mb-3"
                  style={{ fontSize: 32, letterSpacing: 8, fontWeight: 700 }}
                >
                  {modalPin.pin}
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => navigator.clipboard?.writeText(modalPin.pin)}
                    title="Copiar PIN"
                  >
                    <i className="bi bi-clipboard" />
                  </button>
                </div>
                <p className="text-muted small mb-0">
                  <i className="bi bi-clock me-1" />
                  Válido por <strong>{modalPin.expira_horas} horas</strong>. Comunícalo al estudiante.
                </p>
                <div className="alert alert-info small mt-3 mb-0 text-start">
                  <i className="bi bi-info-circle me-1" />
                  El estudiante debe ir a <strong>Campus → ¿Olvidaste tu contraseña?</strong> e ingresar su cédula + este PIN para crear una nueva contraseña.
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-primary w-100" onClick={() => setModalPin(null)}>
                  Entendido
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
